import {
  drillFragmentData,
  ExecutionStatusEnum,
  getFragmentData,
  graphql,
  ServiceUsagesSourceEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';
import type { ResultOf } from '@graphql-typed-document-node/core';

import { resolveServiceUsagesSource } from '../../../components/DocumentEditor/resolvers/utils/resolve-service-usages-source.util';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import type {
  ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment,
  ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
} from '../../../components/ServiceUsages/TotalCard';

import { ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup_Fragment } from '../../../forms/ResolvableServiceUsage/fragments/resolvable-service-usages-group.fragment';

import { selectedOptionGroupIdsOf } from '../../../utils/retained-service-usages-groups.util';
import { isOptionGroupsSelectionEditable } from '../utils/option-groups-selection-editable.util';

import { MissionPage_Tabs_Mission_Fragment } from '../../Mission/Tabs';

export const MissionPage_ServiceUsages_Mission_Fragment = graphql(`
  fragment MissionPage_ServiceUsages_Mission_Fragment on Mission {
    _id

    compositeMission {
      _id

      quotationInfo {
        workflowId
        stateId
      }
    }

    quotationInfo {
      workflowId
      stateId

      snapshot {
        selectedOptionGroupIds

        serviceUsagesGroups {
          _id
          groupingMode
          index
          isOptional
          label
          subMissionId
          totalMode

          serviceUsages {
            ...ServiceUsageCardCmp_ServiceUsage_Fragment
          }
        }

        serviceUsagesGroupTotalCalculations {
          ...ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
        }

        totalCalculation {
          result {
            ...ServiceUsagesTotalCardCmp_Total_Fragment
          }
        }
      }
    }

    servicesInfo {
      contentOriginId
      rejectedOptionGroupIds

      serviceUsagesGroups {
        ...ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup_Fragment
      }

      serviceUsagesGroupTotalCalculations {
        ...ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
      }

      totalCalculation {
        ...ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
      }
    }

    ...MissionForm_Mission_Fragment
  }
`);

export const MissionPage_ServiceUsages_ResolvableMissionType_Fragment = graphql(
  `
    fragment MissionPage_ServiceUsages_ResolvableMissionType_Fragment on ResolvableMissionType {
      serviceUsagesGroups {
        _id
        index
        groupingMode
        isOptional
        label

        serviceUsages {
          ...ServiceUsageCardCmp_ResolvableServiceUsage_Fragment
        }
      }
    }
  `,
);

type MissionPricing = ResultOf<
  typeof MissionPage_ServiceUsages_Mission_Fragment
>;

export const resolveMissionPricing = ({
  mission,
  resolvableMissionType,
  quotationWorkflowsIdMap,
}: Readonly<{
  mission: MissionPricing;
  resolvableMissionType:
    | FragmentType<
        typeof MissionPage_ServiceUsages_ResolvableMissionType_Fragment
      >
    | null
    | undefined;
  quotationWorkflowsIdMap: ReturnType<
    typeof useReferencesContext
  >['quotationWorkflowsIdMap'];
}>) => {
  const { quotationInfo, servicesInfo } = mission;

  const serviceUsagesGroups = getFragmentData(
    ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup_Fragment,
    servicesInfo.serviceUsagesGroups,
  );

  const { source, groupTotalCalculations, totalCalculation } =
    resolveServiceUsagesSource({
      quotationInfo,
      quotationWorkflowsIdMap,
    }) === ServiceUsagesSourceEnum.ServicesInfo
      ? {
          source: ServiceUsagesSourceEnum.ServicesInfo,
          groupTotalCalculations:
            servicesInfo.serviceUsagesGroupTotalCalculations,
          totalCalculation: servicesInfo.totalCalculation,
        }
      : {
          source: ServiceUsagesSourceEnum.QuotationInfo,
          groupTotalCalculations: undefined as
            | readonly FragmentType<
                typeof ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
              >[]
            | undefined,
          // A null snapshot total is one the user may not read.
          totalCalculation: (quotationInfo?.snapshot?.totalCalculation === null
            ? null
            : {
                __typename: 'TotalCalculation',
                details: [
                  {
                    fr: 'Données issues du devis',
                    en: 'Data from quotation',
                  },
                ],
                result: quotationInfo?.snapshot?.totalCalculation?.result,
                status: ExecutionStatusEnum.Ok,
              }) as FragmentType<
            typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
          > | null,
        };

  const fallbackServiceUsagesGroups = getFragmentData(
    MissionPage_ServiceUsages_ResolvableMissionType_Fragment,
    resolvableMissionType,
  )?.serviceUsagesGroups;

  return {
    fallbackServiceUsagesGroups,
    groupTotalCalculations,
    hasQuotation: !!quotationInfo,
    // The edition mode always shows the live groups, even once the quotation
    // snapshot is the consultation source.
    liveGroupTotalCalculations:
      servicesInfo.serviceUsagesGroupTotalCalculations,
    liveTotalCalculation: servicesInfo.totalCalculation,
    // The option choice of the mission's own groups, kept by its pricing as
    // the options it leaves out (an option added later being retained).
    selectedOptionGroupIds: selectedOptionGroupIdsOf(
      serviceUsagesGroups ?? fallbackServiceUsagesGroups ?? EMPTY_ARRAY,
      servicesInfo.rejectedOptionGroupIds,
    ),
    // Decided by the quotation sent to the client: the one of its
    // collaboration for a sub-mission.
    isOptionGroupsSelectionEditable: isOptionGroupsSelectionEditable({
      quotationInfo: mission.compositeMission
        ? mission.compositeMission.quotationInfo
        : quotationInfo,
      quotationWorkflowsIdMap,
    }),
    // The choice applying to its frozen quotation (with the ones of its
    // sub-missions for a collaboration).
    snapshotSelectedOptionGroupIds:
      quotationInfo?.snapshot?.selectedOptionGroupIds ?? null,
    serviceUsagesGroups,
    snapshotServiceUsagesGroups:
      quotationInfo?.snapshot?.serviceUsagesGroups ?? null,
    snapshotGroupTotalCalculations:
      quotationInfo?.snapshot?.serviceUsagesGroupTotalCalculations ?? null,
    totalCalculation,
    source,
  };
};

export const useServiceUsagesConsolidationAndCalculation = () => {
  const { mission: pMission, resolvableMissionType } = useMissionReadyContext();

  const { quotationWorkflowsIdMap } = useReferencesContext();

  const mission = drillFragmentData(
    [
      MissionPage_Tabs_Mission_Fragment,
      MissionPage_ServiceUsages_Mission_Fragment,
    ],
    pMission,
  );

  return resolveMissionPricing({
    mission,
    resolvableMissionType,
    quotationWorkflowsIdMap,
  });
};
